import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AcademicianStartupButton } from "@/components/academician/academicianstartup-button";
import { UploadIcon, XIcon } from "lucide-react";
import { CohortStatus } from "@/components/academician/cohort-table-row";

export interface CohortFormData {
  id?: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  enrollmentDeadline: string;
  price: number;
  capacity: number;
  status: CohortStatus;
  bannerImage?: string;
  moduleIds: string[];
}

interface CohortFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cohort?: CohortFormData;
  availableModules?: Array<{ id: string; name: string }>;
  onSave?: (data: CohortFormData) => void;
  onCancel?: () => void;
}

export function CohortFormModal({
  open,
  onOpenChange,
  cohort,
  availableModules = [],
  onSave,
  onCancel,
}: CohortFormModalProps) {
  const [formData, setFormData] = useState<CohortFormData>({
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    enrollmentDeadline: "",
    price: 0,
    capacity: 20,
    status: "enrolling",
    moduleIds: [],
  });
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (cohort) {
      setFormData(cohort);
      setBannerPreview(cohort.bannerImage || null);
    } else {
      setFormData({
        name: "",
        description: "",
        startDate: "",
        endDate: "",
        enrollmentDeadline: "",
        price: 0,
        capacity: 20,
        status: "enrolling",
        moduleIds: [],
      });
      setBannerPreview(null);
    }
  }, [cohort, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSave?.(formData);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleBannerUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setBannerPreview(result);
        setFormData({ ...formData, bannerImage: result });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeBanner = () => {
    setBannerPreview(null);
    setFormData({ ...formData, bannerImage: undefined });
  };

  const toggleModule = (moduleId: string) => {
    const newModuleIds = formData.moduleIds.includes(moduleId)
      ? formData.moduleIds.filter((id) => id !== moduleId)
      : [...formData.moduleIds, moduleId];
    setFormData({ ...formData, moduleIds: newModuleIds });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {cohort ? "Edit Cohort" : "Create New Cohort"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Cohort Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Cohort Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., Customer Discovery Fundamentals"
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Describe what this cohort is about..."
              rows={4}
              required
            />
          </div>

          {/* Dates Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date *</Label>
              <Input
                id="startDate"
                type="date"
                value={formData.startDate}
                onChange={(e) =>
                  setFormData({ ...formData, startDate: e.target.value })
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date *</Label>
              <Input
                id="endDate"
                type="date"
                value={formData.endDate}
                onChange={(e) =>
                  setFormData({ ...formData, endDate: e.target.value })
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="enrollmentDeadline">Enrollment Deadline *</Label>
              <Input
                id="enrollmentDeadline"
                type="date"
                value={formData.enrollmentDeadline}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    enrollmentDeadline: e.target.value,
                  })
                }
                required
              />
            </div>
          </div>

          {/* Price and Capacity Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="price">Price (USD) *</Label>
              <Input
                id="price"
                type="number"
                min="0"
                step="0.01"
                value={formData.price}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    price: parseFloat(e.target.value),
                  })
                }
                placeholder="499.00"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="capacity">Capacity *</Label>
              <Input
                id="capacity"
                type="number"
                min="1"
                value={formData.capacity}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    capacity: parseInt(e.target.value),
                  })
                }
                placeholder="20"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status *</Label>
              <Select
                value={formData.status}
                onValueChange={(value: CohortStatus) =>
                  setFormData({ ...formData, status: value })
                }
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="enrolling">Enrolling</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Banner Image Upload */}
          <div className="space-y-2">
            <Label>Banner Image</Label>
            {bannerPreview ? (
              <div className="relative">
                <img
                  src={bannerPreview}
                  alt="Banner preview"
                  className="w-full h-48 object-cover rounded-lg border border-border"
                />

                <button
                  type="button"
                  onClick={removeBanner}
                  className="absolute top-2 right-2 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90 transition-colors"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label
                htmlFor="banner"
                className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <UploadIcon className="h-10 w-10 text-muted-foreground mb-3" />
                  <p className="mb-2 text-sm text-muted-foreground">
                    <span className="font-semibold">Click to upload</span> or
                    drag and drop
                  </p>
                  <p className="text-xs text-muted-foreground">
                    PNG, JPG or WEBP (MAX. 1600x600px)
                  </p>
                </div>
                <input
                  id="banner"
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleBannerUpload}
                />
              </label>
            )}
          </div>

          {/* Assign Modules */}
          <div className="space-y-2">
            <Label>Assign Modules</Label>
            <div className="border border-border rounded-lg p-4 space-y-2 max-h-48 overflow-y-auto">
              {availableModules.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No modules available
                </p>
              ) : (
                availableModules.map((module) => (
                  <label
                    key={module.id}
                    className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-2 rounded transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={formData.moduleIds.includes(module.id)}
                      onChange={() => toggleModule(module.id)}
                      className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                    />

                    <span className="text-sm text-foreground">
                      {module.name}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          <DialogFooter>
            <AcademicianStartupButton
              type="button"
              variant="ghost"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </AcademicianStartupButton>
            <AcademicianStartupButton
              type="submit"
              variant="primary"
              loading={isSubmitting}
            >
              {cohort ? "Save Changes" : "Create Cohort"}
            </AcademicianStartupButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
